import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material'
import { Check, Close } from '@mui/icons-material'
import { ReactElement } from 'react'
import { UserAssignmentProgress } from 'src/v3/manage/manage.slice'

interface BulkAssignmentCreditModalProps {
  isOpen: boolean
  selectedUserIDs: string[]
  progressByUsers: UserAssignmentProgress[]
  isSubmitting: boolean
  handleClose: () => void
  handleSubmit: () => void
}

export default function BulkAssignmentCreditModal({
  isOpen,
  selectedUserIDs,
  progressByUsers,
  isSubmitting,
  handleClose,
  handleSubmit,
}: BulkAssignmentCreditModalProps): ReactElement {
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            bgcolor: 'background.default',
          },
        },
      }}
      maxWidth="sm"
    >
      <DialogTitle>
        <Box className="flex items-center justify-between gap-2">
          <Typography variant="h6" component="h1">
            Give Assignment Credit
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className="p-4">
        <Typography className="mb-4">
          You are about to give credit for completing this assignment to{' '}
          <strong>{selectedUserIDs.length}</strong> user(s):
        </Typography>

        <Paper
          className="p-4 mb-4 overflow-auto"
          sx={{ bgcolor: 'background.paperDark', maxHeight: 200 }}
        >
          {selectedUserIDs.map((userID) => {
            let user = progressByUsers.find((u) => u.userID === userID)
            return (
              <Box key={userID} className="py-1 mb-1">
                <Typography variant="body2" className="font-medium">
                  {user?.userName || 'Unknown User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email || ''}
                </Typography>
              </Box>
            )
          })}
        </Paper>

        <Typography variant="body2" color="text.secondary">
          This will mark all courses in this assignment as complete for these
          users. This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions className="gap-2 p-4">
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Check />
            )
          }
        >
          {isSubmitting ? 'Processing...' : 'Give Credit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
